import { useEffect, useState } from "react";
import axios from "axios";
import { X, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { sortHeights } from "@/lib/heights";
import { FIT_OPTIONS } from "@/lib/fit";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { track } from "@/lib/track";
import DuplicateLinkNotice from "@/components/DuplicateLinkNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Familiar hints for the default clothing types; other (shoe) types show label only.
const CLOTHING_TYPE_HINTS = {
  Upper: "Shirt, T-shirt, Hoodie",
  Lower: "Jeans, Pants, Shorts",
};

const ProductSubmissionFormFull = ({ open, onClose, onSuccess }) => {
  useBodyScrollLock(open);
  const [formData, setFormData] = useState({
    product_name: "",
    product_link: "",
    category: "",
    heights: [],
    category_id: "",
    fit: "",
    brand_id: "",
    price: "",
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedImages, setExtractedImages] = useState([]);
  const [selectedExtractedImage, setSelectedExtractedImage] = useState(null);
  const [extractingImages, setExtractingImages] = useState(false);
  const [loading, setLoading] = useState(false);
  // Only live products count as duplicates here: this form is how the admin
  // turns a pending submission into a real product, reusing its link.
  const [duplicate, setDuplicate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/categories`, { withCredentials: true })
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
    axios
      .get(`${API}/brands`, { withCredentials: true })
      .then((res) => setBrands(res.data || []))
      .catch(() => setBrands([]));
  }, [open]);

  // Heights available for selection depend on the chosen Category Tab.
  // If no tab is picked, fall back to the legacy 6.0–6.5 set.
  const DEFAULT_HEIGHTS = ["6.0", "6.1", "6.2", "6.3", "6.4", "6.5"];
  const selectedCat = categories.find((c) => c.id === formData.category_id);
  const formHeights = sortHeights(
    selectedCat?.heights?.length ? selectedCat.heights : DEFAULT_HEIGHTS
  );
  // Type options + size unit follow the chosen tab (clothing vs shoes).
  const formIsShoes = selectedCat?.kind === "shoes";
  const formSizeLabel = (selectedCat?.size_label || "ft").trim() || "ft";
  const formTypes = selectedCat?.types?.length ? selectedCat.types : ["Upper", "Lower"];

  // When category changes, drop any selected heights that no longer apply.
  useEffect(() => {
    if (!formData.category_id) return;
    const allowed = new Set(formHeights);
    const pruned = formData.heights.filter((h) => allowed.has(h));
    if (pruned.length !== formData.heights.length) {
      setFormData((prev) => ({ ...prev, heights: pruned }));
    }
    // Drop a stale type (e.g. "Upper" left over after switching to a shoe tab).
    if (formData.category && !formTypes.includes(formData.category)) {
      setFormData((prev) => ({ ...prev, category: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category_id, categories]);

  useEffect(() => {
    if (open) track("form_start", { form_name: "share_fit_full", form_id: "product_submission_full" });
  }, [open]);

  // Matches the abandon tracking on the other two composers, so the share
  // funnel has the same start -> submit -> abandon shape whichever form opened.
  const handleClose = () => {
    track("form_abandon", {
      form_name: "share_fit_full",
      form_id: "product_submission_full",
      had_link: !!formData.product_link,
      had_image: !!(image || selectedExtractedImage),
    });
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "product_link" && value && value.startsWith("http")) {
      extractImagesFromUrl(value);
    }
  };

  const extractImagesFromUrl = async (url) => {
    setExtractingImages(true);
    // The auto-scrape only works on some retailers, and a silent failure here
    // is the most likely reason a share stalls — so all three outcomes are
    // tracked, keyed by domain so we can see which retailers actually work.
    let domain;
    try {
      domain = new URL(url).hostname;
    } catch (_) {
      domain = undefined;
    }
    track("link_extract_attempt", { form_name: "share_fit_full", link_domain: domain });
    try {
      const response = await axios.post(
        `${API}/extract-images`,
        { url },
        { withCredentials: true }
      );
      if (response.data.success && response.data.images.length > 0) {
        setExtractedImages(response.data.images);
        setSelectedExtractedImage(response.data.images[0]);
        setImagePreview(response.data.images[0]);
        track("link_extract_success", {
          form_name: "share_fit_full",
          link_domain: domain,
          image_count: response.data.images.length,
        });
      } else {
        track("link_extract_empty", { form_name: "share_fit_full", link_domain: domain });
      }
    } catch (error) {
      console.error("Error extracting images:", error);
      track("link_extract_failed", {
        form_name: "share_fit_full",
        link_domain: domain,
        error_status: error?.response?.status,
      });
    } finally {
      setExtractingImages(false);
    }
  };

  const handleExtractedImageSelect = (imgUrl) => {
    setSelectedExtractedImage(imgUrl);
    setImagePreview(imgUrl);
    setImage(null);
  };

  const handleHeightToggle = (height) => {
    const current = formData.heights;
    if (current.includes(height)) {
      setFormData({ ...formData, heights: current.filter((h) => h !== height) });
    } else {
      setFormData({ ...formData, heights: [...current, height] });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setSelectedExtractedImage(null);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.product_name ||
      !formData.product_link ||
      !formData.category ||
      formData.heights.length === 0
    ) {
      track("form_error", { form_name: "share_fit_full", reason: "missing_required" });
      toast.error("Please fill all fields and select at least one height");
      return;
    }
    if (!image && !selectedExtractedImage) {
      track("form_error", { form_name: "share_fit_full", reason: "missing_image" });
      toast.error("Please select or upload an image");
      return;
    }

    if (duplicate) {
      track("form_error", { form_name: "share_fit_full", reason: "duplicate_link" });
      toast.error(
        `That link is already on the site${duplicate.product_name ? ` as "${duplicate.product_name}"` : ""}.`
      );
      return;
    }

    track("form_submit", {
      form_name: "share_fit_full",
      form_id: "product_submission_full",
      height_count: formData.heights.length,
      image_source: image ? "upload" : "scraped",
      has_price: !!formData.price.trim(),
    });

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("product_name", formData.product_name);
      submitData.append("product_link", formData.product_link);
      submitData.append("category", formData.category);
      submitData.append("heights", formData.heights.join(","));
      if (formData.category_id) {
        submitData.append("category_id", formData.category_id);
      }
      if (formData.fit) {
        submitData.append("fit_feedback", formData.fit);
      }
      if (formData.brand_id) {
        submitData.append("brand_id", formData.brand_id);
      }
      if (formData.price.trim()) {
        submitData.append("price", formData.price.trim());
      }
      if (image) {
        submitData.append("image", image);
      } else if (selectedExtractedImage) {
        submitData.append("image_url", selectedExtractedImage);
      }

      await axios.post(`${API}/products/full`, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      track("share", {
        method: "full_form",
        content_type: "fit",
        form_name: "share_fit_full",
      });
      toast.success("Fit shared successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error submitting product:", error);
      track("share_failed", {
        method: "full_form",
        form_name: "share_fit_full",
        error_status: error?.response?.status,
      });
      const detail = error?.response?.data?.detail || "Failed to share fit. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center"
      data-testid="product-form-overlay"
    >
      <div
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="product-form-dialog"
      >
        <div className="flex justify-between items-start mb-8">
          <h2
            className="text-3xl tracking-tighter"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
            data-testid="form-title"
          >
            Share a Fit
          </h2>
          <button
            onClick={handleClose}
            className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
            data-testid="form-close-button"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="product-name-label"
            >
              Product Name
            </label>
            <input
              type="text"
              name="product_name"
              value={formData.product_name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              placeholder="e.g., Levi's 511 Slim Fit Jeans"
              data-testid="product-name-input"
            />
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="product-link-label"
            >
              Product Link
            </label>
            <input
              type="url"
              name="product_link"
              value={formData.product_link}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              placeholder="https://..."
              data-testid="product-link-input"
            />
            <DuplicateLinkNotice
              url={formData.product_link}
              includePending={false}
              onResult={setDuplicate}
            />
          </div>

          {/* Category Tab (admin-managed) */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="category-id-label"
            >
              Category Tab
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="category-id-select"
            >
              <option value="">All (no specific tab)</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-[#6B6A68] mt-1">
              Decides which homepage tab this product shows up in. Leave blank to only appear under &ldquo;All&rdquo;.
            </p>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="category-label"
            >
              Category
            </label>
            <div className="grid grid-cols-2 gap-3">
              {formTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFormData({ ...formData, category: t })}
                  className={`px-4 py-3 border rounded-md transition-all ${
                    formData.category === t
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid={`category-type-${t.toLowerCase().replace(/\s+/g, "-")}-button`}
                >
                  <span className="text-xs uppercase tracking-[0.2em]">{t}</span>
                  {CLOTHING_TYPE_HINTS[t] && (
                    <div className="text-xs text-current/70 mt-1 normal-case tracking-normal">
                      {CLOTHING_TYPE_HINTS[t]}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="height-label"
            >
              {formIsShoes ? "Size Fit (Select all that apply)" : "Height Fit (Select all that apply)"}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {formHeights.map((height) => (
                <button
                  key={height}
                  type="button"
                  onClick={() => handleHeightToggle(height)}
                  className={`px-4 py-3 border rounded-md transition-all text-center ${
                    formData.heights.includes(height)
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid={`height-${height}-button`}
                >
                  <span className="text-sm uppercase tracking-[0.2em]">{formIsShoes ? `${formSizeLabel} ${height}` : `${height} ${formSizeLabel}`}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              How did it fit? (optional)
            </label>
            <div className="space-y-2">
              {FIT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, fit: formData.fit === opt.value ? "" : opt.value })
                  }
                  className={`w-full text-left px-4 py-3 border rounded-md transition-all ${
                    formData.fit === opt.value
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid={`fit-${opt.value}-button`}
                >
                  <span className="text-sm">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Brand (optional)
            </label>
            <select
              value={formData.brand_id}
              onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="brand-select"
            >
              <option value="">No brand</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Price (optional)
            </label>
            <input
              type="text"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              placeholder="e.g., $49 or ₹2,499"
              maxLength={20}
              data-testid="price-input"
            />
            <p className="text-[10px] text-[#6B6A68] mt-1">
              Shown as a tag on the product. Leave blank to hide it.
            </p>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="image-upload-label"
            >
              Product Image {extractingImages && "(Extracting...)"}
            </label>

            {extractedImages.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-[#6B6A68] mb-2">Select from extracted images:</p>
                <div className="grid grid-cols-3 gap-2">
                  {extractedImages.map((imgUrl, index) => (
                    <div
                      key={index}
                      onClick={() => handleExtractedImageSelect(imgUrl)}
                      className={`relative cursor-pointer rounded border-2 transition-all ${
                        selectedExtractedImage === imgUrl
                          ? "border-[#2D302A] ring-2 ring-[#2D302A]"
                          : "border-[#E4E1D9] hover:border-[#2D302A]"
                      }`}
                    >
                      <img
                        src={imgUrl}
                        alt={`Option ${index + 1}`}
                        className="w-full h-20 object-cover rounded"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      {selectedExtractedImage === imgUrl && (
                        <div className="absolute inset-0 bg-[#2D302A]/20 flex items-center justify-center rounded">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                            <path
                              d="M9 12l2 2 4-4"
                              stroke="white"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#6B6A68] mt-2 text-center">or upload your own below</p>
              </div>
            )}

            <div
              className="border-2 border-dashed border-[#E4E1D9] rounded-md p-8 text-center hover:border-[#2D302A] transition-colors cursor-pointer"
              onClick={() => document.getElementById("image-input").click()}
              data-testid="image-upload-area"
            >
              {imagePreview ? (
                <div className="space-y-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded"
                    data-testid="image-preview"
                  />
                  <p className="text-sm text-[#6B6A68]">
                    {image ? "Click to change image" : "Using selected image"}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <UploadSimple size={32} className="mx-auto text-[#6B6A68]" />
                  <p className="text-sm text-[#6B6A68]">Click to upload product image</p>
                </div>
              )}
            </div>
            <input
              id="image-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              data-testid="image-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="submit-fit-button"
          >
            <span className="text-xs uppercase tracking-[0.2em]">
              {loading ? "Sharing..." : "Share Fit"}
            </span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductSubmissionFormFull;
